import React, { memo, useContext, useEffect, useState } from 'react';
import { Button, FormControl } from 'react-bootstrap';
import { faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import axios from 'axios';
import { useAlert } from 'react-alert';
import ProductInformation from '../../../common/product-information/ProductInformation';
import OrderContext from '../OrderContext';
import AuthContext from '../../../authentication/AuthContext';
import UserContext from '../../../user/UserContext';
import { IconWrapper } from '../../../common/IconWrapper';
import AvailabilityIcon from '../../../common/availability-icon/AvailabilityIcon';

export default memo(function OrderLineRow({ product, orderLine, code, upsoldFrom, showAvailability, upsoldId }) {
    const alert = useAlert();
    const cas = useContext(AuthContext);
    const { orderId, refreshOrderInfo } = useContext(OrderContext);
    const { userDetails } = useContext(UserContext);
    const [quantity, setQuantity] = useState(orderLine ? orderLine.quantity : 0);
    const [highlightQuantity, setHighlightQuantity] = useState(false);

    useEffect(() => {
        setQuantity(orderLine ? orderLine.quantity : 0);
        setHighlightQuantity(false);
    }, [orderLine]);

    const numberToPrice = numberField => {
        return '£' + numberField.toFixed(2);
    };

    const isPromo = product.costPromotion || product.rspPromotion;

    let decreaseQuantity = () => {
        if (quantity > 1) {
            setHighlightQuantity(true);
            setQuantity(quantity - 1);
            axios
                .post(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.decrease']
                        .replace('{id}', orderId)
                        .replace('{lineId}', orderLine.orderLineId),
                    {},
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(refreshOrderInfo)
                .catch(() => alert.error('Unable to decrease product quantity'));
        }
        if (quantity === 1) {
            deleteLine();
        }
    };

    let increaseQuantity = () => {
        if (orderLine) {
            setHighlightQuantity(true);
            setQuantity(quantity + 1);
            axios
                .post(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.increase']
                        .replace('{id}', orderId)
                        .replace('{lineId}', orderLine.orderLineId),
                    {},
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(refreshOrderInfo)
                .catch(() => alert.error('Unable to increase product quantity'));
        } else {
            // Can't just assume new quantity is 1 in this case. When the + button is clicked multiple times,
            // the order line hasn't yet been populated, so stringing add calls together is the better solution.
            let newQuantity = quantity + 1;
            setHighlightQuantity(true);
            setQuantity(newQuantity);
            axios
                .put(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.add'].replace('{orderId}', orderId),
                    { id: orderId, quantity: newQuantity, productCode: code, upsoldFrom: upsoldFrom, upsoldId: upsoldId },
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(refreshOrderInfo)
                .catch(() => alert.error('Unable to add product line'));
        }
    };

    let updateQuantity = () => {
        let newQuantity = quantity;
        if (product.supplierMaxOrderQuantity && quantity > product.supplierMaxOrderQuantity) {
            newQuantity = product.supplierMaxOrderQuantity;
            setQuantity(product.supplierMaxOrderQuantity);
        }
        if (orderLine) {
            if (newQuantity > 0) {
                setHighlightQuantity(true);
                axios
                    .put(
                        window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.set']
                            .replace('{id}', orderId)
                            .replace('{lineId}', orderLine.orderLineId),
                        { id: orderId, quantity: newQuantity },
                        {
                            headers: {
                                Authorization: cas,
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                    .then(refreshOrderInfo)
                    .catch(() => alert.error('Unable to add product line'));
            } else {
                deleteLine();
            }
        } else {
            if (newQuantity > 0) {
                setHighlightQuantity(true);
                axios
                    .put(
                        window.CONFIG['ws.activoc-web-order-api.order.lines.add'].replace(
                            '{orderId}',
                            orderId
                        ),
                        {
                            id: orderId,
                            quantity: newQuantity,
                            productCode: code,
                            upsoldFrom: upsoldFrom,
                            upsoldId: upsoldId

                        },
                        {
                            headers: {
                                Authorization: cas,
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                    .then(refreshOrderInfo)
                    .catch(() => alert.error('Unable to add product line'));
            }
        }
    };

    let deleteLine = () => {
        axios
            .delete(
                window.CONFIG['ws.activoc-web-order-api.order.lines.delete']
                    .replace('{id}', orderId)
                    .replace('{lineId}', orderLine.orderLineId),
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(refreshOrderInfo)
            .catch(() => alert.error('ERROR: Unable to remove product from order'));
    };

    return (
        <tr className={isPromo ? 'promo-row' : undefined}>
            <td className={'image-column'}>
                <ProductInformation
                    url={window.CONFIG['ws.activoc-web-order-api.products.full']
                        .replace('{productCode}', code)
                        .replace('{orderId}', orderId)}
                    image={product.image}
                    description={product.productDescription}
                />
            </td>
            <td>{code}</td>
            <td>
                <p>
                    <span className={'promo-icon'}>{isPromo && 'P'}</span>
                    {product.productDescription.toUpperCase()}
                </p>
                <p className={'barcode'}>{product.innerBarcode}</p>
            </td>
            <td>{product.unitSize}</td>
            {userDetails.currentFranchise && userDetails.currentFranchise.rebates && (
                <td>{numberToPrice(product.costIncludingRebate)}</td>
            )}
            <td className={isPromo ? 'promo-cost' : 'greenField'}>{numberToPrice(product.cost)}</td>
            <td className={'greenField'}>
                <p>{numberToPrice(product.rsp)}</p>
                <p className={'barcode greenField'}>
                    {product.maximumAllowableRsp != null &&
                        numberToPrice(product.maximumAllowableRsp) + ' MRP'}
                </p>
            </td>
            <td className={'greenField'}>{product.margin != null && product.margin.toFixed(2) + '%'}</td>
            {userDetails?.currentFranchise?.controlGroups.includes('Availability Control Group') &&
            showAvailability ? (
                <td>
                    <AvailabilityIcon level={product.availability} />
                </td>
            ) : null}
            <td>
                <div className={'flex quantity-column'}>
                    <div className={'quantity-selector flex'}>
                        <Button
                            id={'qty-decrease'}
                            onClick={decreaseQuantity}
                            data-testid={'decrease'}
                            tabIndex={-1}
                            disabled={quantity <= 0}
                        >
                            <IconWrapper className={'qty-decrease'} icon={faMinusCircle} />
                        </Button>
                        <FormControl
                            type={'text'}
                            maxLength={'3'}
                            size={'3'}
                            value={quantity}
                            onChange={event => {
                                let newQuantity = event.target.value;
                                if (newQuantity === '' || isNaN(newQuantity)) {
                                    newQuantity = 0;
                                }
                                if (newQuantity.length > 1 && newQuantity.startsWith('0')) {
                                    newQuantity = newQuantity.substring(1, newQuantity.length);
                                }
                                setQuantity(Number(newQuantity));
                            }}
                            onBlur={updateQuantity}
                            className={
                                (highlightQuantity ? 'quantity-highlight ' : '') +
                                (product.supplierMaxOrderQuantity &&
                                quantity > product.supplierMaxOrderQuantity
                                    ? 'error'
                                    : '')
                            }
                            onFocus={event => event.target.select()}
                        />
                        <Button
                            id={'qty-increase'}
                            onClick={increaseQuantity}
                            data-testid={'increase'}
                            tabIndex={-1}
                            disabled={
                                product.supplierMaxOrderQuantity &&
                                quantity >= product.supplierMaxOrderQuantity
                            }
                        >
                            <IconWrapper icon={faPlusCircle} />
                        </Button>
                    </div>
                    {product.supplierMaxOrderQuantity ? (
                        <span
                            className={quantity > product.supplierMaxOrderQuantity ? 'error-max' : undefined}
                        >
                            Max: {product.supplierMaxOrderQuantity}
                        </span>
                    ) : (
                        ''
                    )}
                </div>
            </td>
        </tr>
    );
});
